import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';

import { Loading } from './Loading';
import { UserContext, SnackbarContext, EngInfoContext } from './userContext';
import {
  AccountHeader,
  EmptyBlock,
  Modal,
  PrimaryButton,
  GhostButton,
  Icon,
} from './myacc/primitives';

function slugify(s) {
  return String(s || '').toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
}

/* ─────────── component ─────────── */

export const MyAccEngInfoBookmarks = () => {
  const history = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);
  const { setEngInfoList } = useContext(EngInfoContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, id: null, title: '' });

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = () => {
    Axios.post('/new_apis/get_bookmarks',
      { user_id: user.user_id },
      { headers: { tkn: user.token } },
    ).then((res) => {
      const s = res.data.status;
      if (s === 1) {
        setData(res.data.data || []);
        setLoading(false);
      } else if (s === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else if (s === 4) {
        setUser(null);
        history('/');
        localStorage.removeItem('estkn');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      }
    });
  };

  const refreshList = () => {
    Axios.post('/new_apis/get_bookmarks',
      { user_id: user.user_id },
      { headers: { tkn: user.token } },
    ).then((res) => {
      if (res.data.status === 1) setEngInfoList(res.data.data.map((d) => d.id));
    });
  };

  const deleteBookmark = () => {
    const id = dialog.id;
    setDialog({ open: false, id: null, title: '' });
    setLoading(true);
    Axios.post('/new_apis/delete_bookmark',
      { user_id: user.user_id, info_id: id },
      { headers: { tkn: user.token } },
    ).then((res) => {
      const s = res.data.status;
      if (s === 1 || s === '1') {
        setSnack({ message: res.data.msg, type: 'success', open: true });
        refreshList();
        fetchData();
      } else if (s === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else if (s === 4) {
        setUser(null);
        history('/');
        localStorage.removeItem('estkn');
        setSnack({ message: res.data.msg, type: 'warning', open: true, direction: 'left' });
      } else {
        setLoading(false);
        setSnack({ message: res.data.msg, type: 'error', open: true });
      }
    });
  };

  if (loading) return <Loading />;

  return (
    <>
      <AccountHeader
        kicker="Saved"
        title="Engineering Info bookmarks"
        subtitle="Your saved topics and references."
      />

      {data.length === 0 ? (
        <EmptyBlock
          icon={<Icon.Bookmark className="h-7 w-7" />}
          title="No bookmarks yet"
          subtitle="Save topics you want to revisit from the Engineering Info library."
          action={
            <RouterLink
              to="/eng-info/home"
              className="
                inline-flex h-10 items-center gap-1.5 rounded-lg px-4
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-[background-color,box-shadow] duration-200
                hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
              "
            >
              Browse Eng Info
              <Icon.ArrowRight className="h-3.5 w-3.5" />
            </RouterLink>
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map((item) => (
            <article
              key={item.id}
              className="
                group relative flex flex-col gap-2.5 overflow-hidden
                rounded-2xl border border-border-subtle bg-surface-paper p-4
                transition-[border-color,transform,box-shadow] duration-200 ease-out-soft
                hover:border-amber/30 hover:-translate-y-0.5 hover:shadow-card-hover
              "
            >
              <span aria-hidden className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-amber/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="flex items-start gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber/10 text-amber">
                  <Icon.Book className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[14px] font-bold leading-[1.35] text-fg-primary line-clamp-2">{item.title}</h3>
                  <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] font-bold text-fg-tertiary">
                    {item.parent}
                  </div>
                </div>
              </div>
              {item.added_date && (
                <div className="inline-flex items-center gap-1 text-[10.5px] text-fg-tertiary">
                  <Icon.Calendar className="h-2.5 w-2.5" />
                  <span>Bookmarked {item.added_date}</span>
                </div>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <RouterLink
                  to={`/eng-info/info-detail/${slugify(item.title)}/${item.id}`}
                  className="
                    inline-flex h-8 items-center gap-1.5 rounded-lg px-3
                    text-[10.5px] font-bold uppercase tracking-[0.1em]
                    bg-amber text-ink
                    transition-[background-color,box-shadow] duration-200
                    hover:bg-amber-light
                  "
                >
                  Continue
                  <Icon.ArrowRight className="h-3 w-3" />
                </RouterLink>
                <button
                  type="button"
                  onClick={() => setDialog({ open: true, id: item.id, title: item.title })}
                  aria-label="Remove bookmark"
                  className="
                    inline-flex h-8 items-center gap-1.5 rounded-lg px-3
                    text-[10.5px] font-bold uppercase tracking-[0.1em]
                    border border-border-subtle text-fg-tertiary
                    transition-[background-color,border-color,color] duration-200
                    hover:border-red-400/40 hover:text-red-400 hover:bg-red-400/8
                  "
                >
                  <Icon.Trash className="h-3 w-3" />
                  Remove
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={dialog.open}
        onClose={() => setDialog({ open: false, id: null, title: '' })}
        title="Remove bookmark?"
        size="xs"
        footer={(
          <>
            <GhostButton onClick={() => setDialog({ open: false, id: null, title: '' })}>Cancel</GhostButton>
            <PrimaryButton onClick={deleteBookmark} icon={<Icon.Trash className="h-4 w-4" />}>Remove</PrimaryButton>
          </>
        )}
      >
        Remove "{dialog.title}" from your bookmarks?
      </Modal>
    </>
  );
};
